// src/pages/Sips.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  LockClosedIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  PlusCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const selectClassName =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
// Modal Genérico
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
      <Card className="relative w-full max-w-2xl">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-3 top-3 z-10"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
        <CardContent className="max-h-[90vh] overflow-y-auto pt-6">
          {children}
        </CardContent>
      </Card>
    </div>
  );
};
// Componente para Associar Usuários a um SIP
const AssociarUsuarios = ({ sip, onSave, onCancel, usuariosDisponiveis }) => {
  const [selectedUsers, setSelectedUsers] = useState(sip?.usuarios || []);
  const handleToggleUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(selectedUsers);
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-2xl font-semibold">Associar Usuários a SIP</h2>
      <div>
        <Label className="text-sm">Selecionar Usuários</Label>
        <div className="mt-2 max-h-60 overflow-y-auto rounded-md border border-border bg-muted/30 p-2">
          {usuariosDisponiveis.map(usuario => (
            <div key={usuario._id} className="flex items-center gap-2 border-b border-border p-2 text-sm text-foreground">
              <input
                type="checkbox"
                id={`usuario-${usuario._id}`}
                checked={selectedUsers.includes(usuario._id)}
                onChange={() => handleToggleUser(usuario._id)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <label htmlFor={`usuario-${usuario._id}`} className="block">
                {usuario.nome} ({usuario.email})
              </label>
            </div>
          ))}
        </div>
      </div>
      <div className="pt-4 flex justify-end gap-4">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit">Salvar</Button>
      </div>
    </form>
  );
};
// Formulário de Criação/Edição de SIP
const SipForm = ({ sip, onSave, onCancel, isSubmitting, clients }) => {
  const [formData, setFormData] = useState({
    nome: '',
    usuario: '',
    senha: '',
    servidor: '',
    porta: 5060,
    protocolo: 'UDP',
    inboundEnabled: true,
    outboundEnabled: true,
    techPrefix: '',
    qtdLigacoesSimultaneas: 1, // Valor padrão
    ...sip
  });
  const [allowedClients, setAllowedClients] = useState(sip?.clientesPermitidos || []);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  const handleClientsChange = (e) => {
    const options = e.target.options;
    const selectedClients = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedClients.push(options[i].value);
      }
    }
    setAllowedClients(selectedClients);
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, clientesPermitidos: allowedClients });
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border-b border-border pb-4">
        <h2 className="text-2xl font-semibold">{sip?._id ? 'Editar Credencial SIP' : 'Criar Nova Credencial SIP'}</h2>
        <p className="text-sm text-muted-foreground mt-1">Preencha os dados da credencial SIP</p>
      </div>
      
      {/* Seção: Informações Básicas */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary border-b border-border pb-2">Informações Básicas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="nome">Nome de Identificação</Label>
            <Input id="nome" name="nome" type="text" value={formData.nome} onChange={handleChange} className="mt-1" required placeholder="Ex: Ramal Suporte" />
          </div>
          <div>
            <Label htmlFor="usuario">Usuário SIP</Label>
            <Input id="usuario" name="usuario" type="text" value={formData.usuario} onChange={handleChange} className="mt-1" required placeholder="Ex: 2001" />
          </div>
        </div>
        <div>
          <Label htmlFor="servidor">Servidor/Host</Label>
          <Input id="servidor" name="servidor" type="text" value={formData.servidor} onChange={handleChange} className="mt-1" required placeholder="sip.seuprovedor.com" />
        </div>
        <div>
          <Label htmlFor="senha">Senha</Label>
          <Input id="senha" name="senha" type="password" value={formData.senha} onChange={handleChange} className="mt-1" required={!sip?._id} placeholder={sip?._id ? 'Deixe em branco para não alterar' : 'Digite a senha do SIP'} />
        </div>
      </div>

      {/* Seção: Configurações de Rede */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary border-b border-border pb-2">Configurações de Rede</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="porta">Porta</Label>
            <Input id="porta" name="porta" type="number" value={formData.porta} onChange={handleChange} className="mt-1" required placeholder="Ex: 5060" />
            <p className="text-xs text-muted-foreground mt-1">Porta padrão: 5060 (UDP/TCP) ou 5061 (TLS)</p>
          </div>
          <div>
            <Label htmlFor="protocolo">Outbound Protocol</Label>
            <select id="protocolo" name="protocolo" value={formData.protocolo} onChange={handleChange} className={`${selectClassName} mt-1`}>
              <option value="UDP">UDP</option>
              <option value="TCP">TCP</option>
              <option value="TLS">TLS</option>
              <option value="TLS/SRTP">TLS/SRTP</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Protocolo de transporte SIP</p>
          </div>
        </div>
        <div>
          <Label htmlFor="techPrefix">Tech Prefix (opcional)</Label>
          <Input
            id="techPrefix"
            name="techPrefix"
            type="text"
            value={formData.techPrefix || ''}
            onChange={handleChange}
            className="mt-1"
            placeholder="Ex: 555"
          />
          <p className="text-xs text-muted-foreground mt-1">Prefixo técnico enviado quando informado.</p>
        </div>
        <div className="space-y-2">
          <Label>Direção das chamadas</Label>
          <div className="flex flex-col gap-2 rounded-md border border-border p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="inboundEnabled"
                checked={!!formData.inboundEnabled}
                onChange={handleCheckboxChange}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              Permitir chamadas de entrada
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="outboundEnabled"
                checked={!!formData.outboundEnabled}
                onChange={handleCheckboxChange}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              Permitir chamadas de saída
            </label>
          </div>
          <p className="text-xs text-muted-foreground">Essas opções serão enviadas para o gateway SIP.</p>
        </div>
      </div>

      {/* Seção: Configurações Avançadas */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary border-b border-border pb-2">Configurações Avançadas</h3>
        <div>
          <Label htmlFor="qtdLigacoesSimultaneas">Quantidade de Ligações Simultâneas</Label>
          <Input id="qtdLigacoesSimultaneas" name="qtdLigacoesSimultaneas" type="number" value={formData.qtdLigacoesSimultaneas} onChange={handleChange} className="mt-1" required min="1" placeholder="Ex: 1" />
          <p className="text-xs text-muted-foreground mt-1">Número máximo de chamadas simultâneas permitidas</p>
        </div>
        {clients?.length > 0 && (
          <div>
            <Label htmlFor="clientesPermitidos">Clientes Autorizados</Label>
            <select
              multiple
              id="clientesPermitidos"
              value={allowedClients}
              onChange={handleClientsChange}
              className={`${selectClassName} mt-1 h-32`}
            >
              {clients.map(c => (
                <option key={c._id} value={c._id}>{c.nome}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">Selecione múltiplos clientes (Ctrl+Click ou Cmd+Click)</p>
          </div>
        )}
      </div>

      {/* Botões de Ação */}
      <div className="pt-4 border-t border-border flex justify-end gap-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting} className="flex items-center gap-2">
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Salvando...
            </>
          ) : (
            'Salvar'
          )}
        </Button>
      </div>
    </form>
  );
};
// Componente Principal
export default function Sips() {
  const [sips, setSips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSip, setEditingSip] = useState(null);
  const [isAssociarUsuariosModalOpen, setIsAssociarUsuariosModalOpen] = useState(false);
  const [usuariosDisponiveis, setUsuariosDisponiveis] = useState([]);
  const [sipParaAssociar, setSipParaAssociar] = useState(null);
  const [clients, setClients] = useState([]);
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData?.id;
  const tokenClient = localStorage.getItem('tokenClient');
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
  // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';

  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.");
      // Opcional: redirecionar para o login
      return null;
    }
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };
  // Função para buscar SIPs
  const fetchSips = useCallback(async () => {
    if (!clienteId) {
      setError('ID do cliente não encontrado. Faça login novamente.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const config = getAuthConfig();
      if (!config) return setLoading(false); // Interrompe se não houver token
      const res = await axios.get(`${baseUrl}/api/sips`, { params: { clienteId, tokenClient }, ...config });
      setSips(res.data);
      setError('');
    } catch (err) {
      setError('Falha ao carregar os SIPs.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clienteId, tokenClient]);
  // Função para buscar usuários
  const fetchUsuarios = async () => {
    setLoading(true);
    const config = getAuthConfig();
    if (!config) return setLoading(false); // Interrompe se não houver token
    try {
      const res = await axios.get(`${baseUrl}/api/usuarios`, { params: { tokenClient }, ...config });
      setUsuariosDisponiveis(res.data);
    } catch (err) {
      console.error('Erro ao buscar usuários:', err);
    }
  };
  // Função para buscar clientes
  const fetchClients = async () => {
    setLoading(true);
    const config = getAuthConfig();
    if (!config) return setLoading(false); // Interrompe se não houver token
    try {
      const res = await axios.get(`${baseUrl}/api/clients`, { params: { tokenClient }, ...config });
      setClients(res.data);
    } catch (err) {
      console.error('Erro ao buscar clientes:', err);
    }
  };
  useEffect(() => {
    fetchSips();
    fetchUsuarios();
    fetchClients();
  }, [fetchSips]);
  const handleOpenCreateModal = () => {
    setEditingSip(null);
    setIsModalOpen(true);
  };
  const handleOpenEditModal = (sip) => {
    setEditingSip(sip);
    setIsModalOpen(true);
  };
  const handleSaveSip = async (formData) => {
    setIsSubmitting(true);
    const toastId = toast.loading(formData._id ? 'Atualizando SIP...' : 'Criando SIP...');
    const isEditing = !!formData._id;
    const url = isEditing ? `${baseUrl}/api/sips/${formData._id}` : `${baseUrl}/api/sips`;
    const method = isEditing ? 'put' : 'post';
    const payload = { ...formData, clienteId, tokenClient };
    try {
      const config = getAuthConfig();
      if (!config) return setIsSubmitting(false); // Interrompe se não houver token
      await axios[method](url, payload, config);
      toast.success(isEditing ? 'SIP atualizado!' : 'SIP criado com sucesso!', { id: toastId });
      setIsModalOpen(false);
      fetchSips();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Ocorreu um erro ao salvar.';
      toast.error(errorMessage, { id: toastId });
      console.error('Erro ao salvar SIP:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDeleteSip = async (sip) => {
    if (window.confirm(`Tem certeza que deseja excluir a credencial SIP "${sip.nome}"?`)) {
      const toastId = toast.loading('Excluindo SIP...');
      try {
        const config = getAuthConfig();
        if (!config) return; // Interrompe se não houver token
        await axios.delete(`${baseUrl}/api/sips/${sip._id}`, {
          data: { tokenClient },  // Passa o tokenClient no corpo da requisição
          ...config
        });
        toast.success('SIP excluído com sucesso!', { id: toastId });
        fetchSips();
      } catch (err) {
        const errorMessage = err.response?.data?.error || 'Ocorreu um erro ao excluir.';
        toast.error(errorMessage, { id: toastId });
        console.error('Erro ao excluir SIP:', err);
      }
    }
  };
  const handleOpenAssociarUsuariosModal = (sip) => {
    setSipParaAssociar(sip);
    setIsAssociarUsuariosModalOpen(true);
  };
  const handleAssociateUsers = async (userIds) => {
    if (!sipParaAssociar) return;
    const toastId = toast.loading('Associando usuários...');
    try {
      const config = getAuthConfig();
      if (!config) return; // Interrompe se não houver token
      await axios.post(`${baseUrl}/api/sips/${sipParaAssociar._id}/associate-users`, { userIds, tokenClient }, config);
      toast.success('Usuários associados com sucesso!', { id: toastId });
      setIsAssociarUsuariosModalOpen(false);
      fetchSips(); // Atualiza a lista de SIPs para refletir as mudanças
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Ocorreu um erro ao associar usuários.';
      toast.error(errorMessage, { id: toastId });
      console.error('Erro ao associar usuários:', err);
    }
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gerenciamento de SIPs</h1>
          <p className="text-sm text-muted-foreground">Gerencie credenciais SIP e associações.</p>
        </div>
        <Button onClick={handleOpenCreateModal}>
          <PlusCircleIcon className="h-5 w-5" />
          Novo SIP
        </Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="p-4 font-semibold">Nome</th>
              <th className="p-4 font-semibold">Usuário</th>
              <th className="p-4 font-semibold">Servidor</th>
              <th className="p-4 font-semibold">ID do Provedor</th>
              <th className="p-4 font-semibold text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="text-center p-8 text-muted-foreground">Carregando SIPs...</td></tr>
            ) : error ? (
              <tr><td colSpan="5" className="text-center p-8 text-destructive"><ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{error}</td></tr>
            ) : sips.length === 0 ? (
              <tr><td colSpan="5" className="text-center p-8 text-muted-foreground">Nenhuma credencial SIP encontrada.</td></tr>
            ) : (
              sips.map((sip) => (
                <tr key={sip._id} className="border-b border-border transition-colors hover:bg-muted/40">
                  <td className="p-4 font-medium">{sip.nome}</td>
                  <td className="p-4">{sip.usuario}</td>
                  <td className="p-4">{sip.servidor}</td>
                  <td className="p-4 font-mono text-sm">{sip.vapiId || 'N/A'}</td>
                  <td className="p-4 text-center flex justify-center items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenEditModal(sip)} title="Editar SIP">
                      <PencilSquareIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteSip(sip)} title="Excluir SIP">
                      <TrashIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleOpenAssociarUsuariosModal(sip)} title="Associar Usuários">
                      <PlusCircleIcon className="h-5 w-5" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      {/* Modal de Criação/Edição */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <SipForm
          sip={editingSip}
          onSave={handleSaveSip}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
          clients={clients}
        />
      </Modal>
      {/* Modal de Associação de Usuários */}
      <Modal isOpen={isAssociarUsuariosModalOpen} onClose={() => setIsAssociarUsuariosModalOpen(false)}>
        <AssociarUsuarios
          sip={sipParaAssociar}
          onSave={handleAssociateUsers}
          onCancel={() => setIsAssociarUsuariosModalOpen(false)}
          usuariosDisponiveis={usuariosDisponiveis}
        />
      </Modal>
    </div>
  );
}
